import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const QUOTES = [
  {
    index: "01",
    quote:
      "SimFinance helped us improve customer acquisition while reducing operational costs.",
    role: "Managing Director",
    org: "Commercial Bank",
    tone: "light",
  },
  {
    index: "02",
    quote: "The verification platform has significantly strengthened our lending process.",
    role: "Head of Credit",
    org: "FinTech Company",
    tone: "navy",
  },
  {
    index: "03",
    quote: "Debt recovery has never been this intelligent.",
    role: "Collections Manager",
    org: "Microfinance Institution",
    tone: "gold",
  },
];

function Testimonials() {
  const reduce = useReducedMotion();
  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  return (
    <section
      id="testimonials"
      className="relative overflow-hidden bg-[#f3f7f5] text-navy"
      aria-labelledby="testimonials-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_0%_0%,rgba(229,184,11,0.14),transparent_55%),radial-gradient(50%_40%_at_100%_80%,rgba(12,31,27,0.07),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.42]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(12,31,27,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(12,31,27,0.07) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(12,31,27,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(12,31,27,0.08) 1px, transparent 1px)",
            backgroundSize: "84px 84px",
            backgroundPosition: "-1px -1px",
          }}
        />
        <div className="absolute left-[5%] top-[12%] h-28 w-28 border border-navy/10" />
        <div className="absolute left-[8%] top-[16%] h-14 w-14 border border-gold/45" />
        <div className="absolute right-[6%] top-[18%] h-24 w-24 border border-royal/20" />
        <div className="absolute bottom-[10%] right-[8%] h-20 w-20 border border-gold/30" />
      </div>

      <div className="relative mx-auto max-w-8xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <motion.header
          className="max-w-3xl"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
            Customer testimonials
          </p>
          <h2
            id="testimonials-heading"
            className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,5vw,3.5rem)]"
          >
            Institutions already
            <span className="mt-1 block">trust the ecosystem</span>
          </h2>
        </motion.header>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {QUOTES.map((quote, index) => (
            <QuoteCard key={quote.index} quote={quote} reduce={reduce} delay={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function QuoteCard({ quote, reduce, delay }) {
  const tone = quote.tone;
  const surface =
    tone === "navy"
      ? "bg-navy text-white"
      : tone === "gold"
        ? "bg-gold text-navy"
        : "bg-white text-navy";

  return (
    <motion.figure
      className={`relative flex flex-col overflow-hidden border p-6 sm:p-7 ${
        tone === "navy"
          ? "border-navy"
          : tone === "gold"
            ? "border-gold"
            : "border-navy/10"
      } ${surface}`}
      initial={reduce ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={reduce ? { duration: 0 } : { duration: 0.45, delay: 0.06 * delay, ease: EASE }}
    >
      {tone === "navy" ? (
        <div
          className="pointer-events-none absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />
      ) : null}

      <div className="relative flex items-center justify-between">
        <span
          className={`flex h-9 w-9 items-center justify-center text-[11px] font-semibold tracking-[0.12em] ${
            tone === "navy"
              ? "bg-gold text-navy"
              : tone === "gold"
                ? "bg-navy text-white"
                : "bg-navy text-white"
          }`}
        >
          {quote.index}
        </span>
        <span
          aria-hidden="true"
          className={`font-semibold leading-none ${
            tone === "gold" ? "text-navy/25" : tone === "navy" ? "text-gold/70" : "text-gold"
          } [font-size:2.5rem]`}
        >
          “
        </span>
      </div>

      <blockquote className="relative mt-6 flex-1 text-xl font-semibold leading-snug tracking-tight">
        {quote.quote}
      </blockquote>

      <figcaption
        className={`relative mt-8 border-t pt-4 ${
          tone === "navy" ? "border-white/15" : tone === "gold" ? "border-navy/15" : "border-navy/10"
        }`}
      >
        <p className="text-sm font-semibold">{quote.role}</p>
        <p
          className={`mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${
            tone === "navy" ? "text-gold" : "text-navy/40"
          }`}
        >
          {quote.org}
        </p>
      </figcaption>
    </motion.figure>
  );
}

export default Testimonials;
